import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ModelChip, ModelLogo, providerName } from '@shared/brand';
import { compact, idr } from '@shared/format';
import { Alert, CodeBlock, Icon, Loading, Segmented } from '../../components/ui';
import { Callout, DocSection, DocTable, EndpointList } from '../../components/DocBlocks';
import { API_KEY_ENV, BASE_URL, DEFAULT_MODEL } from '../../snippets';
import { AppLink, PageHead, pretty, useDocs, type CatalogModel, type Modality } from './context';

/* Autentikasi */

export function AuthPage() {
  return (
    <>
      <PageHead slug="autentikasi">Setiap request membawa API key di header Authorization.</PageHead>

      <DocSection id="header" title="Kirim key di header">
        <CodeBlock label="Header" code="Authorization: Bearer aia_live_xxxxxxxxxxxxxxxx" />
        <p>
          Buat key per proyek di <AppLink to="/api-keys">menu API Key</AppLink>. Kami hanya menyimpan hash-nya, jadi key yang
          hilang tidak bisa ditampilkan lagi. Cabut key itu, lalu buat yang baru.
        </p>
        <p>
          Key berawalan <code>aia_live_</code> untuk proyek PRODUCTION dan <code>aia_test_</code> untuk proyek DEVELOPMENT.
          Keduanya memakai saldo dan model yang sama.
        </p>
        <Callout title="Simpan key di server" tone="warn">
          Jangan taruh key di kode frontend, aplikasi mobile, atau repositori Git. Siapa pun yang membuka aplikasinya bisa
          mengambil key itu. Panggil API dari server Anda.
        </Callout>
      </DocSection>

      <DocSection id="cek" title="Cek key Anda">
        <p>Request ini membalas daftar model bila key Anda benar.</p>
        <CodeBlock label="cURL" code={`curl ${BASE_URL}/models \\\n  -H "Authorization: Bearer $${API_KEY_ENV}"`} />
      </DocSection>

      <DocSection
        id="batasi-model"
        title="Membatasi model per API key"
        lead="Secara bawaan satu key bisa memanggil semua model. Batasi key ke model yang memang dipakai aplikasinya, supaya biaya tetap terkendali bila key salah konfigurasi atau bocor."
      >
        <h3>Lewat portal</h3>
        <ol>
          <li>
            Buka <AppLink to="/api-keys">API Key</AppLink>, lalu klik <b>Buat key</b>, atau <b>Ubah</b> pada key yang sudah ada.
          </li>
          <li>
            Di <b>Akses model</b>, pilih <b>Hanya model tertentu</b>, lalu centang modelnya.
          </li>
          <li>Simpan. Perubahan berlaku untuk request berikutnya, tanpa mengganti key.</li>
        </ol>
        <h3>Lewat API</h3>
        <p>
          Isi <code>allowed_models</code> dengan ID model saat membuat atau mengubah key. <code>null</code> atau <code>[]</code>{' '}
          mengembalikan akses ke semua model. ID yang tidak dikenal dibalas 400. Endpoint ini memakai token login portal, bukan API key.
        </p>
        <EndpointList
          items={[
            { method: 'POST', path: '/v1/api-keys', note: 'Buat key baru' },
            { method: 'PATCH', path: '/v1/api-keys/{id}', note: 'Ubah key yang ada' },
          ]}
        />
        <CodeBlock
          label="PATCH /v1/api-keys/{id}"
          code={pretty({ allowed_models: ['gemini-3.1-flash-lite', 'deepseek-flash'] })}
        />
        <h3>Yang terjadi pada request</h3>
        <ul>
          <li>
            Model di luar daftar ditolak dengan <code>403 model_not_allowed</code> sebelum provider dipanggil, jadi tidak ada biaya.
            Pesannya menyebut model yang diizinkan.
          </li>
          <li>
            <code>GET /v1/models</code> dengan key ini hanya menampilkan model yang diizinkan.
          </li>
          <li>
            Router <code>auto:</code> dan model cadangan hanya memilih dari daftar ini. Aturan yang sama berlaku untuk gambar dan video.
          </li>
        </ul>
        <CodeBlock
          label="403 · application/json"
          code={pretty({
            error: {
              message: 'This API key is not allowed to use "gpt-5.5". Allowed models: gemini-3.1-flash-lite, deepseek-flash.',
              type: 'permission_error',
              code: 'model_not_allowed',
              param: null,
              request_id: 'req_01M4…',
              details: { allowed_models: ['gemini-3.1-flash-lite', 'deepseek-flash'] },
            },
          })}
        />
      </DocSection>

      <DocSection id="hak-akses" title="API key dan login portal">
        <p>Keduanya terpisah dan tidak bisa saling menggantikan.</p>
        <DocTable
          className="docs-access"
          head={['Kredensial', 'Bisa dipakai untuk', 'Tidak bisa']}
          rows={[
            {
              key: 'key',
              cells: [
                <span className="docs-cell-title">API key</span>,
                <>
                  Chat, gambar, video, serta membaca <code>/v1/models</code>, <code>/v1/balance</code>, dan{' '}
                  <code>/v1/limits</code>
                </>,
                'Membuat key, isi saldo, mengubah proyek',
              ],
            },
            {
              key: 'login',
              cells: [
                <span className="docs-cell-title">Login portal</span>,
                'Mengelola akun, key, proyek, dan saldo di portal ini',
                'Menjalankan model lewat API',
              ],
            },
          ]}
        />
        <p>
          Pemisahan ini disengaja. Key yang bocor tidak bisa dipakai untuk membuat key lain atau menguras saldo lewat top up.
        </p>
      </DocSection>
    </>
  );
}

/* Memilih model */

const FAMILIES = [
  { family: 'claude', name: 'Claude', provider: 'anthropic', kind: 'Teks' },
  { family: 'gpt', name: 'GPT', provider: 'openai', kind: 'Teks' },
  { family: 'gemini', name: 'Gemini', provider: 'google', kind: 'Teks' },
  { family: 'grok', name: 'Grok', provider: 'xai', kind: 'Teks' },
  { family: 'deepseek', name: 'DeepSeek', provider: 'deepseek', kind: 'Teks' },
  { family: 'kimi', name: 'Kimi', provider: 'moonshot', kind: 'Teks' },
  { family: 'glm', name: 'GLM', provider: 'zai', kind: 'Teks' },
  { family: 'seedream', name: 'Seedream', provider: 'byteplus', kind: 'Gambar' },
  { family: 'seedance', name: 'Seedance', provider: 'byteplus', kind: 'Video' },
];

function Families() {
  return (
    <ul className="docs-families">
      {FAMILIES.map((family) => (
        <li key={family.family} className="docs-family">
          <ModelLogo family={family.family} provider={family.provider} size={34} />
          <span className="docs-family-text">
            <span className="docs-family-name">{family.name}</span>
            <span className="docs-family-meta">
              {providerName(family.provider)} · {family.kind}
            </span>
          </span>
          <code className="docs-family-id">{family.family}-*</code>
        </li>
      ))}
    </ul>
  );
}

const KIND_LABEL: Record<Modality, string> = { text: 'Teks', image: 'Gambar', video: 'Video', embedding: 'Embedding' };

function CatalogTable({ models }: { models: CatalogModel[] }) {
  const [kind, setKind] = useState<'all' | Modality>('all');
  const count = (m: Modality) => models.filter((model) => (model.modality ?? 'text') === m).length;
  const order: Modality[] = ['text', 'embedding', 'image', 'video'];
  const present = order.filter((m) => count(m) > 0);
  const sorted = [...models].sort((a, b) => order.indexOf(a.modality ?? 'text') - order.indexOf(b.modality ?? 'text'));
  const rows = kind === 'all' ? sorted : sorted.filter((model) => (model.modality ?? 'text') === kind);

  return (
    <>
      <div className="docs-catalog-bar">
        {present.length > 1 ? (
          <Segmented
            label="Jenis model"
            value={kind}
            onChange={setKind}
            options={[
              { id: 'all', label: `Semua ${models.length}` },
              ...present.map((m) => ({ id: m, label: `${KIND_LABEL[m]} ${count(m)}` })),
            ]}
          />
        ) : (
          <span className="docs-catalog-count">{models.length} model tersedia</span>
        )}
        <Link to="/models" className="btn btn-sm btn-ghost">
          Buka halaman Model
          <Icon name="arrow" size={14} />
        </Link>
      </div>
      <div className="docs-box">
        <div className="table-wrap">
          <table className="docs-catalog">
            <thead>
              <tr>
                <th>Model</th>
                <th className="num">Konteks</th>
                <th>Fitur</th>
                <th className="num">Harga</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((model) => {
                const modality = model.modality ?? 'text';
                const caps = model.capabilities ?? {};
                const p = model.pricing ?? {};
                return (
                  <tr key={model.id}>
                    <td>
                      <ModelChip model={model.id} name={model.display_name} provider={model.provider} family={model.family} size={28} />
                    </td>
                    <td className="num">{(modality === 'text' || modality === 'embedding') && model.context_window ? compact(model.context_window) : '—'}</td>
                    <td>
                      {modality === 'text' ? (
                        <span className="docs-caps">
                          {caps.stream && <span className="tag">stream</span>}
                          {caps.tools && <span className="tag">tools</span>}
                          {caps.vision && <span className="tag">vision</span>}
                          {caps.json_schema && <span className="tag">json</span>}
                          {caps.reasoning && <span className="tag">reasoning</span>}
                        </span>
                      ) : (
                        <span className="muted">
                          {modality === 'video' ? 'Job async' : modality === 'embedding' ? `${model.max_output_tokens ?? '—'} dimensi` : 'Sinkron'}
                        </span>
                      )}
                    </td>
                    <td className="docs-price">
                      {modality === 'text' && (
                        <>
                          <div>
                            {idr(p.input_per_1m)}
                            <small> masuk</small>
                          </div>
                          <div>
                            {idr(p.output_per_1m)}
                            <small> keluar</small>
                          </div>
                        </>
                      )}
                      {modality === 'embedding' && (
                        <div>
                          {idr(p.input_per_1m)}
                          <small> masuk</small>
                        </div>
                      )}
                      {modality === 'image' && (
                        <div>
                          {idr(p.per_image)}
                          <small> per gambar</small>
                        </div>
                      )}
                      {modality === 'video' && (
                        <>
                          <div>
                            {idr(p.per_1m_video_tokens)}
                            <small> per 1 jt token video</small>
                          </div>
                          {p.per_second_720p !== undefined && (
                            <div>
                              <small>≈ {idr(p.per_second_720p)} per detik 720p</small>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="docs-caption">
        Harga teks per 1 juta token, diambil langsung dari <code>GET /v1/models</code> untuk akun Anda.
      </p>
    </>
  );
}

export function ModelsPage() {
  const { publicView, models, catalogError, model, text } = useDocs();

  const backups = useMemo(() => {
    const others = (models ?? []).filter((m) => (m.modality ?? 'text') === 'text' && m.id !== model && m.owned_by !== 'mock');
    const firstOf = (provider?: string) => others.find((m) => m.provider !== provider && m.provider !== text?.provider);
    const a = firstOf();
    const b = a ? firstOf(a.provider) : undefined;
    return a && b ? [a.id, b.id] : ['gpt-5.5', 'gemini-3.8-flash'];
  }, [models, model, text]);

  return (
    <>
      <PageHead slug="model">
        Isi <code>model</code> dengan ID model tertentu, atau biarkan router memilihkan untuk Anda.
      </PageHead>

      <DocSection
        id="katalog"
        title="Model yang tersedia"
        lead={
          publicView
            ? 'Keluarga model yang bisa Anda panggil. Daftar lengkap dengan ID dan harga terkini muncul setelah Anda masuk.'
            : 'Semua model yang bisa dipanggil akun Anda saat ini.'
        }
      >
        {publicView ? (
          <>
            <Families />
            <p className="docs-caption">
              ID model berawalan nama keluarganya, misalnya <code>{DEFAULT_MODEL}</code>. Program Anda bisa membaca daftar lengkap
              lewat <code>GET /v1/models</code>.
            </p>
          </>
        ) : catalogError ? (
          <Alert kind="error">Gagal memuat katalog: {catalogError}</Alert>
        ) : models ? (
          <CatalogTable models={models} />
        ) : (
          <Loading label="Memuat katalog" />
        )}
      </DocSection>

      <DocSection id="auto" title="Biarkan router memilih">
        <p>Isi <code>model</code> dengan salah satu alias ini. Router memilih model untuk setiap request.</p>
        <DocTable
          className="docs-aliases"
          head={['Nilai model', 'Yang dipilih']}
          rows={[
            { key: 'cheap', cells: [<code>auto:cheapest</code>, 'Perkiraan biaya terendah untuk request ini.'] },
            { key: 'fast', cells: [<code>auto:fastest</code>, 'Latensi rata-rata terendah.'] },
            {
              key: 'bal',
              cells: [
                <span className="docs-alias-pair">
                  <code>auto:balanced</code>
                  <span className="muted">atau</span>
                  <code>auto</code>
                </span>,
                'Gabungan biaya, latensi, dan tingkat keberhasilan (bobot 50/30/20).',
              ],
            },
          ]}
        />
        <p>
          Router hanya memilih model yang mendukung kebutuhan request, misalnya streaming, tools, atau input gambar. Provider yang
          sedang gangguan dilewati. Field <code>model</code> dan <code>provider</code> di respons menyebut model yang akhirnya
          menjawab.
        </p>
      </DocSection>

      <DocSection id="fallback" title="Model cadangan">
        <p>
          Pasang daftar model cadangan per proyek di <AppLink to="/projects">menu Proyek</AppLink>. Bila model utama gagal karena
          error yang aman diulang, request dicoba ke model berikutnya. Contohnya rate limit provider, timeout, atau provider
          sedang penuh.
        </p>
        <p>Error karena isi request, misalnya prompt terlalu panjang, tidak memicu fallback.</p>
        <CodeBlock
          label="Body request"
          code={pretty({
            model,
            messages: [{ role: 'user', content: 'Halo' }],
            fallback_models: backups,
          })}
        />
        <p>
          <code>fallback_models</code> menerima sampai 5 model. Isinya hanya bisa mempersempit atau mengurutkan ulang daftar
          proyek. Model yang tidak ada di daftar proyek diabaikan, jadi pemegang key tidak bisa memperluas pilihan pemilik
          proyek.
        </p>
        <Callout>
          Model yang Anda sebut namanya tidak pernah diganti diam-diam. Tanpa daftar cadangan, error dikembalikan apa adanya.
        </Callout>
      </DocSection>
    </>
  );
}
